
import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import utc from "dayjs/plugin/utc";
import {
  Row, Col, Form, Input, DatePicker, Card, Table, Tag, Button, Space,
  Typography, Select, Tabs, Badge, Tooltip, Divider, Dropdown, Menu,
} from "antd";
import {
  EyeOutlined, PlusOutlined, DollarOutlined, BellOutlined, DownOutlined,
  FileTextOutlined, SendOutlined, CheckCircleOutlined, WalletOutlined,
  ClockCircleOutlined, ExclamationCircleOutlined, BankOutlined, DownloadOutlined, CalendarOutlined,
} from "@ant-design/icons";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip,
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { InvoiceService } from "../../../Services/Manage/InvoiceService";
import { UserService } from "../../../Services/Manage/UserService";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(utc);

const getPermission = (link) => {
  try {
    const raw = localStorage.getItem("ome_permission");
    const parsed = JSON.parse(raw);
    const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
    return permissions.find(p => p.link === link) || { add: false, edit: false, delete: false, read: false };
  } catch { return { add: false, edit: false, delete: false, read: false }; }
};

const ListAllInvoice = () => {
  const perm = getPermission('/invoice');
  const viewAll = perm.view_all === true;
  const navigate = useNavigate();
  const [allInvoice, setAllInvoice] = useState([]);
  const [filteredInvoice, setFilteredInvoice] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [dateFilterType, setDateFilterType] = useState("invoice_date");
  const [activeTab, setActiveTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState(null);
  const [customerFilter, setCustomerFilter] = useState(null);
  const [moreActionsLabel, setMoreActionsLabel] = useState("More Actions");

  const getAllInvoive = async () => {
    try {
      setLoading(true);
      const user = await UserService.getOmeUser();
      const request = viewAll
        ? await InvoiceService.getAllInvoice()
        : await InvoiceService.getAllInvoiceById(user?.uid);

      let invoicesData = [];
      if (Array.isArray(request.data)) {
        invoicesData = request.data.map(item => ({ ...item, key: item.id }));
      } else if (request.data && typeof request.data === 'object') {
        invoicesData = [{ ...request.data, key: request.data.id }];
      }

      setAllInvoice(invoicesData);
      setFilteredInvoice(invoicesData);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllInvoive();
  }, []);

  const applyQuickDatePreset = (range, label) => {
    setDateFilterType("invoice_date");
    setDateRange(range);
    setMoreActionsLabel(label);
    applyAllFilters(searchText, range, "invoice_date", statusFilter, customerFilter);
  };

  const applyAllTimeFilter = () => {
    setDateRange(null);
    setMoreActionsLabel("More Actions");
    applyAllFilters(searchText, null, dateFilterType, statusFilter, customerFilter);
  };


  const applyAllFilters = (search = searchText, range = dateRange, type = dateFilterType, status = statusFilter, customer = customerFilter) => {
    let filtered = [...allInvoice];

    if (search) {
      const lower = search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.invoice_no?.toLowerCase().includes(lower) ||
          item.customer_name?.toLowerCase().includes(lower) ||
          item.status?.toLowerCase().includes(lower)
      );
    }

    if (range && range.length === 2) {
      const [start, end] = range;
      filtered = filtered.filter((item) => {
        const dateField = item[type];
        if (!dateField) return false;
        const d = dayjs(dateField);
        return d.isSameOrAfter(start.startOf("day")) && d.isSameOrBefore(end.endOf("day"));
      });
    }

    if (status) filtered = filtered.filter(item => item.status === status);
    if (customer) filtered = filtered.filter(item => (item.customer_name || "-") === customer);

    setFilteredInvoice(filtered);
  };

  const columns = [
    {
      title: "S. No",
      key: "si_no",
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    { title: "Invoice No", dataIndex: "invoice_no", key: "invoice_no", render: (v) => <span style={{ color: '#1677ff', fontWeight: 600 }}>{v}</span> },
    {
      title: "Quotation No",
      dataIndex: "source_qu_no",
      key: "source_qu_no",
      render: (source_qu_no, record) => source_qu_no || (record.qu_no === record.invoice_no ? "-" : record.qu_no),
    },
    {
      title: "Customer Name",
      key: "customer_name",
      render: (_, record) => record.customer_contact_person || record.customer_name || "-",
    },
    { title: "Invoice Date", dataIndex: "invoice_date", key: "invoice_date", render: (date) => date ? dayjs(date).format("DD-MM-YYYY") : "-" },
    { title: "Due Date", dataIndex: "due_date", key: "due_date", render: (date) => date ? dayjs(date).format("DD-MM-YYYY") : "-" },
    {
      title: "Invoice Value",
      key: "invoice_value",
      align: "right",
      render: (_, record) => `₹ ${Number(record.grand_total || 0).toLocaleString('en-IN')}`,
    },
    {
      title: "Collected / Pending",
      key: "payment_summary",
      render: (_, record) => {
        const payments = record.payments || [];
        const totalCollected = payments.reduce((sum, p) => sum + Number(p.paid_amt || 0), 0);
        const grandTotal = Number(record.grand_total || 0);
        const pending = grandTotal - totalCollected;
        return (
          <>
            <div style={{ fontSize: 12 }}>Collected: <strong style={{ color: '#52c41a' }}>₹{totalCollected.toLocaleString('en-IN')}</strong></div>
            <div style={{ fontSize: 12 }}>Pending: <strong style={{ color: '#fa8c16' }}>₹{pending.toLocaleString('en-IN')}</strong></div>
          </>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      align: 'center',
      render: (_, record) => {
        const computed = computeListStatus(record);
        return (
          <Tag style={{ fontSize: 12, padding: '4px 10px', fontWeight: 600 }} color={getStatusColor(computed)}>{computed}</Tag>
        );
      },
    },
    { title: "Added By", dataIndex: "added_by_name", key: "added_by_name", render: (name) => name || "-" },
    { title: "Added On", dataIndex: "added_on", key: "added_on", render: (date) => date ? dayjs(date).format("DD-MM-YYYY") : "-" },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button onClick={() => navigate('/invoice/view', { state: { id: record.invoice_no } })} icon={<EyeOutlined />} size="middle">
            View
          </Button>
        </Space>
      ),
    },
  ];

  const getStatusColor = (status) => {
    const statusColors = {
      'Created': 'blue', 'Sent': 'blue', 'Paid': 'green', 'Paid Partially': 'orange',
      'Pending': 'orange', 'Overdue': 'red', 'Cancelled': 'red', 'Draft': 'default',
    };
    return statusColors[status] || 'default';
  };

  // ==================== DASHBOARD COMPUTATIONS ====================
  const collectedOf = (inv) => (inv.payments || []).reduce((s, p) => s + Number(p.paid_amt || 0), 0);
  const pendingOf = (inv) => Number(inv.grand_total || 0) - collectedOf(inv);
  const isOverdueInv = (inv) => inv.due_date && dayjs(inv.due_date).isBefore(dayjs(), 'day') && pendingOf(inv) > 0;

  const computeListStatus = (inv) => {
    if (!inv?.id) return "Draft";
    const grandTotal = Number(inv.grand_total || 0);
    const received = collectedOf(inv);
    const due = inv.due_date;
    const duePassed = due && dayjs(due).isBefore(dayjs(), 'day');

    if (grandTotal > 0 && received >= grandTotal) return "Paid";
    if (received > 0 && received < grandTotal) return duePassed ? "Overdue" : "Paid Partially";
    if (duePassed && received <= 0) return "Overdue";
    return inv.status || "Draft";
  };

const draftCount = filteredInvoice.filter(d => computeListStatus(d) === 'Draft').length;
  const sentCount = filteredInvoice.filter(d => computeListStatus(d) === 'Sent' || computeListStatus(d) === 'Created').length;
  const paidCount = filteredInvoice.filter(d => computeListStatus(d) === 'Paid').length;
  const partiallyPaidCount = filteredInvoice.filter(d => computeListStatus(d) === 'Paid Partially').length;
  const overdueCount = filteredInvoice.filter(d => computeListStatus(d) === 'Overdue').length;

  const directInvoices = filteredInvoice.filter(d => (d.source || "quotation") === "manual");
  const directInvoiceCount = directInvoices.length;
  const directInvoiceValue = directInvoices.reduce((s, d) => s + Number(d.grand_total || 0), 0);

  const totalInvoiceValue = filteredInvoice.reduce((s, d) => s + Number(d.grand_total || 0), 0);
  const collectedAmount = filteredInvoice.reduce((s, d) => s + collectedOf(d), 0);
  const pendingAmount = filteredInvoice.reduce((s, d) => s + pendingOf(d), 0);
  const overdueAmount = filteredInvoice.filter(isOverdueInv).reduce((s, d) => s + pendingOf(d), 0);
  const collectionRate = totalInvoiceValue ? ((collectedAmount / totalInvoiceValue) * 100).toFixed(0) : 0;

  const thisMonthStart = dayjs().startOf('month');
  const todayStart = dayjs().startOf('day');

const todaysCollection = filteredInvoice.reduce((s, d) => s + (d.payments || []).filter(p => dayjs(p.payment_date || p.added_on).isSame(dayjs(), 'day')).reduce((a, p) => a + Number(p.paid_amt || 0), 0), 0);
  const thisMonthCollection = filteredInvoice.reduce((s, d) => s + (d.payments || []).filter(p => dayjs(p.payment_date || p.added_on).isSameOrAfter(thisMonthStart)).reduce((a, p) => a + Number(p.paid_amt || 0), 0), 0);
  const pendingCollection = pendingAmount;
  const overdueCollection = overdueAmount;

  const row1 = [
    { title: "Draft Invoices", value: draftCount, color: "#1677ff", bg: "#f0f7ff", icon: <FileTextOutlined /> },
    { title: "Sent Invoices", value: sentCount, color: "#13c2c2", bg: "#f0fffe", icon: <SendOutlined /> },
    { title: "Paid Invoices", value: paidCount, color: "#52c41a", bg: "#f6ffed", icon: <CheckCircleOutlined /> },
    { title: "Partially Paid", value: partiallyPaidCount, color: "#fa8c16", bg: "#fff7e6", icon: <ClockCircleOutlined /> },
    { title: "Overdue Invoices", value: overdueCount, color: "#f5222d", bg: "#fff1f0", icon: <ExclamationCircleOutlined /> },
    { title: "Direct Invoices", value: directInvoiceCount, color: "#722ed1", bg: "#f9f0ff", icon: <FileTextOutlined /> },
  ];

  const row2 = [
    { title: "Total Direct Invoice Value", value: `₹ ${directInvoiceValue.toLocaleString('en-IN')}`, sub: "This Month", color: "#722ed1", bg: "#f9f0ff", icon: <FileTextOutlined /> },
    { title: "Total Invoice Value", value: `₹ ${totalInvoiceValue.toLocaleString('en-IN')}`, sub: "This Month", color: "#1677ff", bg: "#f0f7ff", icon: <WalletOutlined /> },
    { title: "Collected Amount", value: `₹ ${collectedAmount.toLocaleString('en-IN')}`, sub: "This Month", color: "#52c41a", bg: "#f6ffed", icon: <BankOutlined /> },
    { title: "Pending Amount", value: `₹ ${pendingAmount.toLocaleString('en-IN')}`, sub: "This Month", color: "#fa8c16", bg: "#fff7e6", icon: <ClockCircleOutlined /> },
    { title: "Overdue Amount", value: `₹ ${overdueAmount.toLocaleString('en-IN')}`, sub: "This Month", color: "#f5222d", bg: "#fff1f0", icon: <ExclamationCircleOutlined /> },
    { title: "Collection Rate", value: `${collectionRate}%`, sub: "This Month", color: "#722ed1", bg: "#f9f0ff", icon: <DollarOutlined /> },
  ];

  const StatCard = ({ title, value, sub, color, bg, icon }) => (
    <Card bodyStyle={{ padding: '10px 12px' }} style={{ borderRadius: 8, border: '1px solid #f0f0f0', height: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>{title}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: color, lineHeight: 1.2 }}>{value}</div>
          {sub && <div style={{ fontSize: 10, color: '#888', marginTop: 3 }}>{sub}</div>}
        </div>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: color, flexShrink: 0 }}>
          {icon}
        </div>
      </div>
    </Card>
  );

  // ---- Monthly Revenue Trend (by invoice_date) ----
  const monthlyMap = {};
  filteredInvoice.forEach(d => {
    if (!d.invoice_date) return;
    const key = dayjs(d.invoice_date).format("MMM");
    monthlyMap[key] = (monthlyMap[key] || 0) + Number(d.grand_total || 0);
  });
  const monthOrder = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const monthlyData = monthOrder.filter(m => monthlyMap[m] !== undefined).map(m => ({ month: m, value: monthlyMap[m] }));

  // ---- Invoice Status Distribution (donut) ----
  const distColors = { Paid: "#52c41a", "Paid Partially": "#fa8c16", Pending: "#1677ff", Created: "#1677ff", Draft: "#8c8c8c", Overdue: "#f5222d" };
  const distMap = {};
  filteredInvoice.forEach(d => {
    const st = computeListStatus(d);
    distMap[st] = (distMap[st] || 0) + 1;
  });
  const statusDistData = Object.entries(distMap).map(([name, value]) => ({ name, value, color: distColors[name] || "#bfbfbf" }));
  const distTotal = statusDistData.reduce((a, b) => a + b.value, 0);

  // ---- Aging Analysis (pending amount buckets) ----
  const today = dayjs();
  const agingBuckets = [
    { label: "0–15 Days", color: "#52c41a", min: 0, max: 15 },
    { label: "16–30 Days", color: "#1677ff", min: 16, max: 30 },
    { label: "31–60 Days", color: "#fa8c16", min: 31, max: 60 },
    { label: "60+ Days", color: "#f5222d", min: 61, max: 99999 },
  ];
 const agingData = agingBuckets.map(b => {
    const val = filteredInvoice
      .filter(d => pendingOf(d) > 0 && d.due_date)
      .filter(d => {
        const age = today.diff(dayjs(d.due_date), 'day');
        return age >= b.min && age <= b.max;
      })
      .reduce((s, d) => s + pendingOf(d), 0);
    return { name: b.label, value: val, color: b.color };
  }).filter(b => b.value > 0);
  const agingTotal = agingData.reduce((a, b) => a + b.value, 0);

  // ---- Top Customers by Revenue ----
 const custMap = {};
  filteredInvoice.forEach(d => {
    const key = d.customer_name || "Unknown";
    custMap[key] = (custMap[key] || 0) + Number(d.grand_total || 0);
  });
  const topCustomers = Object.entries(custMap).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 5);
  const topCustMax = Math.max(...topCustomers.map(c => c.value), 1);

  // ---- Service-wise Revenue (by project name; falls back to "Others" — never the qu_no/reference) ----
 const svcMap = {};
  filteredInvoice.forEach(d => {
    (d.line_items || []).forEach(it => {
      const key = it.service_name || "Others";
      svcMap[key] = (svcMap[key] || 0) + Number(it.totalprice || 0);
    });
  });
  const svcSorted = Object.entries(svcMap).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  const svcTop = svcSorted.slice(0, 5);
  const svcOthersValue = svcSorted.slice(5).reduce((s, x) => s + x.value, 0);
  const serviceWiseData = [
    ...svcTop,
    ...(svcOthersValue > 0 ? [{ name: "Others", value: svcOthersValue }] : []),
  ];
  const svcMax = Math.max(...serviceWiseData.map(s => s.value), 1);

const upcomingDue = filteredInvoice
    .filter(d => d.due_date && pendingOf(d) > 0 && dayjs(d.due_date).isSameOrAfter(todayStart))
    .sort((a, b) => dayjs(a.due_date).valueOf() - dayjs(b.due_date).valueOf())
    .slice(0, 4);

  // ---- Overdue Invoices ----
  const overdueList = filteredInvoice
    .filter(isOverdueInv)
    .sort((a, b) => dayjs(a.due_date).valueOf() - dayjs(b.due_date).valueOf())
    .slice(0, 4);

  // ---- Recent Payments ----
  const recentPayments = filteredInvoice
    .flatMap(d => (d.payments || []).map(p => ({ ...p, invoice_no: d.invoice_no, customer_name: d.customer_name })))
    .sort((a, b) => dayjs(b.added_on || b.payment_date).valueOf() - dayjs(a.added_on || a.payment_date).valueOf())
    .slice(0, 4);

  // ---- Top Overdue Customers ----
  const overdueCustMap = {};
  filteredInvoice.filter(isOverdueInv).forEach(d => {
    const key = d.customer_name || "Unknown";
    if (!overdueCustMap[key]) overdueCustMap[key] = { amount: 0, maxDays: 0 };
    overdueCustMap[key].amount += pendingOf(d);
    const days = today.diff(dayjs(d.due_date), 'day');
    overdueCustMap[key].maxDays = Math.max(overdueCustMap[key].maxDays, days);
  });
  const topOverdueCustomers = Object.entries(overdueCustMap)
    .map(([name, v]) => ({ name, ...v }))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 4);

  const customerOptions = [...new Set(allInvoice.map(d => d.customer_name).filter(Boolean))].map(c => ({ label: c, value: c }));

  const REPORT_COLUMNS = [
    { label: "Invoice No" },
    { label: "Quotation No" },
    { label: "Customer" },
    { label: "Invoice Date" },
    { label: "Due Date" },
    { label: "Invoice Value (Rs.)" },
    { label: "Collected (Rs.)" },
    { label: "Pending (Rs.)" },
    { label: "Status" },
  ];

  const buildReportRows = () => filteredInvoice
    .filter((inv) => activeTab === "all" || (inv.source || "quotation") === activeTab)
    .map((d) => ({
      "Invoice No": d.invoice_no || "-",
      "Quotation No": d.source_qu_no || (d.qu_no === d.invoice_no ? "-" : d.qu_no) || "-",
      "Customer": d.customer_contact_person || d.customer_name || "-",
      "Invoice Date": d.invoice_date ? dayjs(d.invoice_date).format("DD-MM-YYYY") : "-",
      "Due Date": d.due_date ? dayjs(d.due_date).format("DD-MM-YYYY") : "-",
      "Invoice Value (Rs.)": Number(d.grand_total || 0),
      "Collected (Rs.)": collectedOf(d),
      "Pending (Rs.)": pendingOf(d),
      "Status": computeListStatus(d),
    }));

  const reportDateStamp = () => dayjs().format("YYYY-MM-DD");

  const handleExportExcel = () => {
    const rows = buildReportRows();
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Invoices Report");
    XLSX.writeFile(wb, `Invoices-Report-${reportDateStamp()}.xlsx`);
  };

  const handleExportPDF = () => {
    const rows = buildReportRows();
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text("Invoices Report", 14, 14);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString('en-GB')}`, 14, 20);
    autoTable(doc, {
      startY: 26,
      head: [REPORT_COLUMNS.map((c) => c.label)],
      body: rows.map((r) => REPORT_COLUMNS.map((c) => r[c.label])),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [22, 88, 180] },
    });
    doc.save(`Invoices-Report-${reportDateStamp()}.pdf`);
  };

  const exportMenuItems = [
    { key: "excel", label: "Download as Excel (.xlsx)", onClick: handleExportExcel },
    { key: "pdf", label: "Download as PDF", onClick: handleExportPDF },
  ];

  // ---- GST / Recurring Revenue / Avg Collection summary cards ----
  // Note: invoices don't store a separate tax breakdown column here, so GST is
  // approximated assuming grand_total is tax-inclusive at 18% GST.
  const GST_RATE = 18;
  const extractGst = (amount) => {
    const total = Number(amount || 0);
    const base = total / (1 + GST_RATE / 100);
    return total - base;
  };
  const totalGstCollected = filteredInvoice.reduce((s, d) => s + extractGst(collectedOf(d)), 0);
  const totalGstPayable = filteredInvoice.reduce((s, d) => s + extractGst(pendingOf(d)), 0);

  const monthlyRecurringRevenue = filteredInvoice
    .filter(d => d.invoice_date && dayjs(d.invoice_date).isSameOrAfter(thisMonthStart))
    .reduce((s, d) => s + Number(d.grand_total || 0), 0);

  const paidInvoicesWithDates = filteredInvoice.filter(d => {
    const lastPayment = (d.payments || []).slice().sort((a, b) => dayjs(b.payment_date || b.created_at).valueOf() - dayjs(a.payment_date || a.created_at).valueOf())[0];
    return d.invoice_date && lastPayment && (lastPayment.payment_date || lastPayment.created_at);
  });
  const avgCollectionDays = paidInvoicesWithDates.length
    ? Math.round(
      paidInvoicesWithDates.reduce((s, d) => {
        const lastPayment = d.payments.slice().sort((a, b) => dayjs(b.payment_date || b.created_at).valueOf() - dayjs(a.payment_date || a.created_at).valueOf())[0];
        const days = dayjs(lastPayment.payment_date || lastPayment.created_at).diff(dayjs(d.invoice_date), 'day');
        return s + Math.max(days, 0);
      }, 0) / paidInvoicesWithDates.length
    )
    : 0;

  const row3 = [
    { title: "Total GST Collected", value: `₹ ${totalGstCollected.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, sub: "This Month", color: "#722ed1", bg: "#f9f0ff", icon: <FileTextOutlined /> },
    { title: "Total GST Payable", value: `₹ ${totalGstPayable.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, sub: "This Month", color: "#fa8c16", bg: "#fff7e6", icon: <FileTextOutlined /> },
    { title: "Monthly Recurring Revenue", value: `₹ ${monthlyRecurringRevenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, sub: "This Month", color: "#1677ff", bg: "#f0f7ff", icon: <BankOutlined /> },
    { title: "Avg Collection Days", value: `${avgCollectionDays} Days`, sub: "This Month", color: "#13c2c2", bg: "#f0fffe", icon: <ClockCircleOutlined /> },
  ];

  return (
    <Card bordered={false} bodyStyle={{ padding: 0 }}>
      {/* Header */}
      <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0, color: '#1658b4' }}>Invoice Dashboard</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>Dashboard &gt; Invoice Dashboard</Typography.Text>
        </div>
        <Space wrap>
          <Tooltip title={!perm.add ? "You don't have permission to create" : ""}>
            <Button
              size="middle" type="primary" icon={<PlusOutlined />}
              onClick={() => navigate('/invoice/create')} disabled={!perm.add}
              style={{ background: '#1677ff', borderColor: '#1677ff' }}
            >
              Create Invoice
            </Button>
          </Tooltip>
          <Button size="middle" type="primary" icon={<DollarOutlined />} style={{ background: '#52c41a', borderColor: '#52c41a' }}>
            Record Payment
          </Button>
          <Button size="middle" type="primary" icon={<BellOutlined />} style={{ background: '#722ed1', borderColor: '#722ed1' }}>
            Send Reminder
          </Button>
          <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
            <Button size="middle" icon={<DownloadOutlined />}>Export</Button>
          </Dropdown>
          <Dropdown
            trigger={['click']}
            overlay={
              <Menu
                onClick={({ key }) => {
                  if (key === 'today') applyQuickDatePreset([dayjs().startOf('day'), dayjs().endOf('day')], 'Today');
                  else if (key === 'last7') applyQuickDatePreset([dayjs().subtract(6, 'day').startOf('day'), dayjs()], 'Last 7 Days');
                  else if (key === 'last30') applyQuickDatePreset([dayjs().subtract(29, 'day').startOf('day'), dayjs()], 'Last 30 Days');
                  else if (key === 'thisMonth') applyQuickDatePreset([dayjs().startOf('month'), dayjs().endOf('month')], 'This Month');
                  else if (key === 'thisYear') applyQuickDatePreset([dayjs().startOf('year'), dayjs()], 'This Year');
                  else if (key === 'all') applyAllTimeFilter();
                }}
                items={[
                  { key: 'all', label: <span><CalendarOutlined style={{ marginRight: 6 }} />All Time</span> },
                  { key: 'today', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Today</span> },
                  { key: 'last7', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last 7 Days</span> },
                  { key: 'last30', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last 30 Days</span> },
                  { key: 'thisMonth', label: <span><CalendarOutlined style={{ marginRight: 6 }} />This Month</span> },
                  { key: 'thisYear', label: <span><CalendarOutlined style={{ marginRight: 6 }} />This Year</span> },
                ]}
              />
            }
          >
            <Button size="middle" icon={<DownOutlined />}>
              {moreActionsLabel}
            </Button>
          </Dropdown>
        </Space>
      </div>

      {/* Stat Cards */}
      <div style={{ padding: '8px 16px 0' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
          {row1.map((card, i) => (
            <div key={i} style={{ flex: 1, minWidth: 0 }}><StatCard {...card} /></div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {row2.map((card, i) => (
            <div key={i} style={{ flex: 1, minWidth: 0 }}><StatCard {...card} /></div>
          ))}
        </div>
      </div>

      {/* Charts Row 1: Collection Summary / Monthly Revenue Trend / Invoice Status Distribution */}
      <div style={{ padding: '6px 16px 0' }}>
        <Row gutter={[8, 8]}>
          <Col xs={24} lg={8}>
            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
              <Typography.Text strong>Collection Summary</Typography.Text>
              <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                {[
                  { label: "Today's Collection", value: todaysCollection, color: '#52c41a', bg: '#f6ffed', icon: <BankOutlined /> },
                  { label: "This Month Collection", value: thisMonthCollection, color: '#1677ff', bg: '#f0f7ff', icon: <BankOutlined /> },
                  { label: "Pending Collection", value: pendingCollection, color: '#fa8c16', bg: '#fff7e6', icon: <ClockCircleOutlined /> },
                  { label: "Overdue Collection", value: overdueCollection, color: '#f5222d', bg: '#fff1f0', icon: <ExclamationCircleOutlined /> },
                ].map((c, i) => (
                  <Col span={12} key={i}>
                    <div style={{ background: c.bg, borderRadius: 8, padding: '8px 10px' }}>
                      <div style={{ fontSize: 11, color: c.color, marginBottom: 4 }}>{c.label}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: c.color, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        ₹ {c.value.toLocaleString('en-IN')} <span style={{ fontSize: 14 }}>{c.icon}</span>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography.Text strong>Monthly Revenue Trend</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>This Year</Typography.Text>
              </div>
              {monthlyData.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 150, color: '#bbb', fontSize: 13 }}>
                  <span style={{ fontSize: 28 }}>📈</span>
                  <span style={{ marginTop: 6 }}>No data yet</span>
                </div>
              ) : (
                <div style={{ width: '100%', height: 160, marginTop: 8 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={monthlyData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="invVal" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#1677ff" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#1677ff" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} width={40}
                        tickFormatter={(v) => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                      <RechartsTooltip contentStyle={{ fontSize: 11, padding: '4px 8px', borderRadius: 6 }} formatter={(v) => [`₹ ${Number(v).toLocaleString('en-IN')}`, 'Value']} />
                      <Area type="monotone" dataKey="value" stroke="#1677ff" strokeWidth={2} fill="url(#invVal)" dot={{ r: 4, fill: '#1677ff' }} activeDot={{ r: 5 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
              <Typography.Text strong>Invoice Status Distribution</Typography.Text>
              {distTotal === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13, marginTop: 8 }}>
                  <span style={{ fontSize: 28 }}>📊</span>
                  <span style={{ marginTop: 6 }}>No data yet</span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
                  <div style={{ width: 120, height: 120, flexShrink: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <RechartsTooltip contentStyle={{ fontSize: 11, padding: '4px 8px', borderRadius: 6 }} formatter={(value, name) => [`${value} (${distTotal ? Math.round((value / distTotal) * 100) : 0}%)`, name]} />
                        <Pie data={statusDistData} dataKey="value" nameKey="name" innerRadius={32} outerRadius={55} paddingAngle={2}>
                          {statusDistData.map((e, i) => <Cell key={i} fill={e.color} />)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={{ flex: 1 }}>
                    {statusDistData.map((e, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, marginBottom: 5 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ color: e.color, fontSize: 14 }}>●</span>
                          <span style={{ color: '#333' }}>{e.name}</span>
                        </span>
                        <span style={{ fontWeight: 700, color: '#333' }}>{distTotal ? Math.round((e.value / distTotal) * 100) : 0}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </Col>
        </Row>

        {/* Charts Row 2: Aging / Top Customers / Service-wise Revenue */}
        <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
          <Col xs={24} lg={8}>
            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
              <Typography.Text strong>Aging Analysis (Pending Amount)</Typography.Text>
              {agingTotal === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13, marginTop: 8 }}>
                  <span style={{ fontSize: 28 }}>📊</span>
                  <span style={{ marginTop: 6 }}>No pending dues</span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
                  <div style={{ width: 120, height: 120, flexShrink: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <RechartsTooltip contentStyle={{ fontSize: 11, padding: '4px 8px', borderRadius: 6 }} formatter={(value, name) => [`₹ ${Number(value).toLocaleString('en-IN')}`, name]} />
                        <Pie data={agingData} dataKey="value" nameKey="name" innerRadius={32} outerRadius={55} paddingAngle={2}>
                          {agingData.map((e, i) => <Cell key={i} fill={e.color} />)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={{ flex: 1 }}>
                    {agingData.map((e, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, marginBottom: 5 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ color: e.color, fontSize: 14 }}>●</span>
                          <span style={{ color: '#333' }}>{e.name}</span>
                        </span>
                        <span style={{ fontWeight: 600, color: '#333' }}>₹ {e.value.toLocaleString('en-IN')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </Col>

            <Col xs={24} lg={8}>
              <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                <Typography.Text strong>Top Customers by Revenue</Typography.Text>
                <div style={{ marginTop: 10 }}>
                  {topCustomers.length === 0 ? (
                    <Typography.Text type="secondary" style={{ fontSize: 13 }}>No data yet</Typography.Text>
                  ) : (
                    topCustomers.map((c, i) => (
                      <div key={i} style={{ marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                          <span style={{ color: '#333' }}>{c.name}</span>
                          <span style={{ fontWeight: 700, color: '#333' }}>₹ {c.value.toLocaleString('en-IN')}</span>
                        </div>
                        <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3 }}>
                          <div style={{ width: `${Math.max((c.value / topCustMax) * 100, 4)}%`, height: '100%', background: '#1677ff', borderRadius: 3 }} />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </Col>

          <Col xs={24} lg={8}>
            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
              <Typography.Text strong>Service-wise Revenue</Typography.Text>
              <div style={{ marginTop: 10 }}>
                {serviceWiseData.length === 0 ? (
                  <Typography.Text type="secondary" style={{ fontSize: 13 }}>No data yet</Typography.Text>
                ) : (
                  serviceWiseData.map((s, i) => (
                    <div key={i} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                        <span style={{ color: '#333' }}>{s.name}</span>
                        <span style={{ fontWeight: 700, color: '#333' }}>₹ {s.value.toLocaleString('en-IN')}</span>
                      </div>
                      <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3 }}>
                        <div style={{ width: `${Math.max((s.value / svcMax) * 100, 4)}%`, height: '100%', background: '#52c41a', borderRadius: 3 }} />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </Col>
        </Row>
        
        {/* Charts Row 3: Upcoming Due / Overdue / Recent Payments / Top Overdue Customers */}
        <Row gutter={[6, 6]} style={{ marginTop: 8 }} align="stretch">
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: '6px 10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography.Text strong style={{ fontSize: 13 }}>Upcoming Due Invoices</Typography.Text>
                <Button type="link" size="small" style={{ padding: 0, fontSize: 10 }}>View All</Button>
              </div>
              <div style={{ marginTop: 6 }}>
                {upcomingDue.length === 0 ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>No upcoming dues</Typography.Text>
                ) : upcomingDue.map((inv, i) => (
                  <div key={i} style={{ padding: '6px 0', borderBottom: i < upcomingDue.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#1677ff' }}>{inv.invoice_no}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#333' }}>₹{pendingOf(inv).toLocaleString('en-IN')}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#777' }}>{inv.customer_name || '-'}</div>
                    <div style={{ fontSize: 10, color: '#fa8c16' }}>Due in {dayjs(inv.due_date).diff(today, 'day')} day(s)</div>
                  </div>
                ))}
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={6}>
            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: '6px 10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography.Text strong style={{ fontSize: 13 }}>Overdue Invoices</Typography.Text>
                <Button type="link" size="small" style={{ padding: 0, fontSize: 11 }}>View All</Button>
              </div>
              <div style={{ marginTop: 8 }}>
                {overdueList.length === 0 ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>No overdue invoices</Typography.Text>
                ) : overdueList.map((inv, i) => (
                  <div key={i} style={{ padding: '6px 0', borderBottom: i < overdueList.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#1677ff' }}>{inv.invoice_no}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#f5222d' }}>₹{pendingOf(inv).toLocaleString('en-IN')}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#777' }}>{inv.customer_name || '-'}</div>
                    <div style={{ fontSize: 10, color: '#f5222d' }}>{today.diff(dayjs(inv.due_date), 'day')} days overdue</div>
                  </div>
                ))}
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={6}>
            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: '6px 10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography.Text strong style={{ fontSize: 13 }}>Recent Payments</Typography.Text>
                <Button type="link" size="small" style={{ padding: 0, fontSize: 11 }}>View All</Button>
              </div>
              <div style={{ marginTop: 8 }}>
                {recentPayments.length === 0 ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>No recent payments</Typography.Text>
                ) : recentPayments.map((p, i) => (
                  <div key={i} style={{ padding: '6px 0', borderBottom: i < recentPayments.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#52c41a' }}>₹{Number(p.paid_amt || 0).toLocaleString('en-IN')}</span>
                      <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />
                    </div>
                    <div style={{ fontSize: 11, color: '#777' }}>{p.customer_name || '-'}</div>
                    <div style={{ fontSize: 10, color: '#999' }}>
                      {p.added_on || p.payment_date ? dayjs.utc(p.added_on || p.payment_date).local().format("DD MMM, hh:mm A") : "-"}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={6}>
            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: '6px 10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography.Text strong style={{ fontSize: 13 }}>Top Overdue Customers</Typography.Text>
                <Button type="link" size="small" style={{ padding: 0, fontSize: 11 }}>View All</Button>
              </div>
              <div style={{ marginTop: 8 }}>
                {topOverdueCustomers.length === 0 ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>No overdue customers</Typography.Text>
                ) : topOverdueCustomers.map((c, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < topOverdueCustomers.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
                    <span style={{ fontSize: 12, color: '#333' }}>{c.name}</span>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#f5222d' }}>₹{c.amount.toLocaleString('en-IN')}</div>
                      <div style={{ fontSize: 10, color: '#fa8c16' }}>{c.maxDays} Days</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Row 3: GST / Recurring Revenue / Avg Collection summary cards */}
      <div style={{ padding: '6px 16px 0' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {row3.map((card, i) => (
            <div key={i} style={{ flex: 1, minWidth: 0 }}><StatCard {...card} /></div>
          ))}
        </div>
      </div>

      <div style={{ padding: '8px 16px 16px' }}>
        <Divider />

        <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Search Invoice" layout="vertical" style={{ marginBottom: 0 }}>
                <Input
                  allowClear
                  placeholder="Search invoices..."
                  value={searchText}
                  onChange={(e) => {
                    const value = e.target.value.toLowerCase();
                    setSearchText(value);
                    applyAllFilters(value, dateRange, dateFilterType, statusFilter, customerFilter);
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Form.Item label="All Status" layout="vertical" style={{ marginBottom: 0 }}>
                <Select
                  allowClear
                  placeholder="All Status"
                  style={{ width: "100%" }}
                  value={statusFilter}
                  options={[
                    { label: "Draft", value: "Draft" },
                    { label: "Sent", value: "Created" },
                    { label: "Paid", value: "Paid" },
                    { label: "Paid Partially", value: "Paid Partially" },
                    { label: "Overdue", value: "Overdue" },
                  ]}
                  onChange={(val) => { setStatusFilter(val); applyAllFilters(searchText, dateRange, dateFilterType, val, customerFilter); }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Form.Item label="All Customers" layout="vertical" style={{ marginBottom: 0 }}>
                <Select
                  allowClear
                  placeholder="All Customers"
                  style={{ width: "100%" }}
                  value={customerFilter}
                  options={customerOptions}
                  onChange={(val) => { setCustomerFilter(val); applyAllFilters(searchText, dateRange, dateFilterType, statusFilter, val); }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item label="Date Range" layout="vertical" style={{ marginBottom: 0 }}>
                <DatePicker.RangePicker
                  style={{ width: "100%" }}
                  value={dateRange}
                  onChange={(dates) => { setDateRange(dates); applyAllFilters(searchText, dates, dateFilterType, statusFilter, customerFilter); }}
                  format="DD-MM-YYYY"
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Tabs
          activeKey={activeTab}
          onChange={(key) => { setActiveTab(key); setCurrentPage(1); }}
          items={[
            {
              key: "all",
              label: (
                <Badge count={filteredInvoice.length} offset={[5, -2]} showZero color="purple">
                  <span style={{ paddingRight: 10 }}>All</span>
                </Badge>
              ),
            },
            {
              key: "quotation",
              label: (
                <Badge count={filteredInvoice.filter((inv) => (inv.source || "quotation") === "quotation").length} offset={[5, -2]} showZero color="blue">
                  <span style={{ paddingRight: 10 }}>Quotation</span>
                </Badge>
              ),
            },
            {
              key: "enquiry",
              label: (
                <Badge count={filteredInvoice.filter((inv) => (inv.source || "quotation") === "enquiry").length} offset={[5, -2]} showZero color="green">
                  <span style={{ paddingRight: 10 }}>Enquiry</span>
                </Badge>
              ),
            },
            {
              key: "manual",
              label: (
                <Badge count={filteredInvoice.filter((inv) => (inv.source || "quotation") === "manual").length} offset={[5, -2]} showZero color="orange">
                  <span style={{ paddingRight: 10 }}>Direct Invoice</span>
                </Badge>
              ),
            },
          ]}
        />

        <Table
          columns={columns}
          dataSource={filteredInvoice
            .filter((inv) => activeTab === "all" || (inv.source || "quotation") === activeTab)
            .slice()
            .sort((a, b) => new Date(b.updated_on) - new Date(a.updated_on))}
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            onChange: (page, size) => { setCurrentPage(page); setPageSize(size); },
          }}
        />
      </div>
    </Card>
  );
};

export default ListAllInvoice;